import { useState } from "react";
import { Check, ChevronRight, ClipboardCheck, Flag, HelpCircle, Loader2, MessageCircleQuestion, Reply, X } from "lucide-react";
import { toast } from "sonner";
import { AgentAvatar } from "@/components/agent-avatar";
import { QuestionAge, QuestionCardChip } from "@/components/pending-question";
import { Avatar, EmptyState, Skeleton } from "@/components/ui/misc";
import { Page, PageHeader } from "@/components/ui/page";
import { api, ApiError } from "@/lib/api";
import { useBoardsQuery } from "@/hooks/use-boards";
import { useQuickMove } from "@/hooks/use-quick-move";
import { useCan } from "@/lib/auth";
import { doneTarget } from "@/lib/column-targets";
import { relativeTime } from "@/lib/format";
import { avatarOf } from "@/lib/avatars";
import { questionsOf, questionTitle, reasonKinds, WAITING_REASON_LABELS } from "@/lib/waiting";
import {
  kindColor,
  priorityColor,
  type AgentWithContext,
  type User,
  type WaitingCard,
  type WaitingChatQuestion,
  type WaitingReasonKind,
  type WaitingReviewCard,
} from "@/lib/types";
import { cn } from "@/lib/utils";

const REASON_ICON: Record<WaitingReasonKind, typeof Flag> = { question: HelpCircle, reply: Reply, flag: Flag };

/**
 * Every card, on every board, that is waiting on the user's answer — oldest
 * first, because the one waiting longest is the one most owed a reply. A row
 * opens the card's sheet, where the question can be answered in place. An open
 * question an agent asked in its chat about no listed card is a row too, in its
 * own group, opening that chat — or the badge would count what the page hides.
 */
export function WaitingView({
  cards,
  questions,
  reviews = [],
  loaded,
  agents,
  onChanged,
  users,
  now,
  onOpenTask,
  onOpenAgent,
  showTitle,
}: {
  cards: WaitingCard[];
  questions: WaitingChatQuestion[];
  /** Cards in a review column: their own section, counted apart from the questions. */
  reviews?: WaitingReviewCard[];
  loaded: boolean;
  /** After a question is dismissed here: re-read the list. */
  onChanged?: () => void;
  /** For the asking agent's avatar and status. */
  agents: AgentWithContext[];
  users: User[];
  now: number;
  onOpenTask: (taskId: string) => void;
  /** The agent's chat, scrolled to the question. */
  onOpenAgent: (agentId: string, messageId?: string | null) => void;
  /** The tab layout's top bar already names the view. */
  showTitle: boolean;
}) {
  return (
    <Page>
      <PageHeader
        title="Awaiting your answer"
        showTitle={showTitle}
        description="Questions agents asked, in their chats or on cards, replies ready to send and cards flagged for you."
      />
      {!loaded ? (
        <div className="space-y-1.5">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-16" />
          ))}
        </div>
      ) : cards.length === 0 && questions.length === 0 && reviews.length === 0 ? (
        <EmptyState icon={<MessageCircleQuestion />} title="Nothing is waiting on you." className="py-12" />
      ) : (
        <div className="space-y-5">
          {questions.length > 0 ? (
            <section>
              <GroupHeading label="In agent chats" count={questions.length} />
              <ul className="space-y-1.5">
                {questions.map((question) => (
                  <li key={question.messageId}>
                    <ChatQuestionRow
                      question={question}
                      agents={agents}
                      users={users}
                      now={now}
                      onOpen={() => onOpenAgent(question.agentId, question.messageId)}
                      onOpenTask={onOpenTask}
                      onDismissed={onChanged}
                    />
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {/* Then by where the card is: one group per board, oldest wait first within it. */}
          {byBoard(cards).map((group) => (
            <section key={group.boardId}>
              <GroupHeading label={`On ${group.boardName}`} count={group.cards.length} />
              <ul className="space-y-1.5">
                {group.cards.map((card) => (
                  <li key={card.taskId}>
                    <WaitingRow card={card} agents={agents} users={users} now={now} onOpen={() => onOpenTask(card.taskId)} />
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {cards.length === 0 && questions.length === 0 ? (
            <p className="rounded-md border border-dashed border-border/80 px-3 py-4 text-center text-[13px] text-muted-foreground">
              No questions waiting on you.
            </p>
          ) : null}
          {reviews.length > 0 ? <ReviewSection reviews={reviews} users={users} now={now} onOpenTask={onOpenTask} /> : null}
        </div>
      )}
    </Page>
  );
}

/** Boards in the order their longest-waiting card came in; the cards keep their order. */
function byBoard(cards: WaitingCard[]): Array<{ boardId: string; boardName: string; cards: WaitingCard[] }> {
  const groups = new Map<string, { boardId: string; boardName: string; cards: WaitingCard[] }>();
  for (const card of cards) {
    const group = groups.get(card.boardId) ?? { boardId: card.boardId, boardName: card.boardName, cards: [] };
    group.cards.push(card);
    groups.set(card.boardId, group);
  }
  return [...groups.values()];
}


/**
 * Work that someone finished and that waits on the user to accept it. Its own
 * section and its own, quieter colour: forty reviews are a queue to work through,
 * not forty people waiting on an answer, and must not drown out one real question.
 * Collapsible, and it remembers.
 */
export function ReviewSection({
  reviews,
  users,
  now,
  onOpenTask,
  title = "Ready for your review",
  storageKey = "automation.awaiting.reviewsOpen",
}: {
  reviews: WaitingReviewCard[];
  users: User[];
  now: number;
  onOpenTask: (taskId: string) => void;
  /** The same cards read "To review" on My tasks. */
  title?: string;
  /** Where the open/closed state is remembered, per page. */
  storageKey?: string;
}) {
  const [open, setOpen] = useState(() => localStorage.getItem(storageKey) !== "0");
  const canMove = useCan("member");
  const boardsQuery = useBoardsQuery();
  const quickMove = useQuickMove();
  /** Rows marked done here, hidden until the list refetches without them (or Undo brings them back). */
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());
  const setRowHidden = (taskId: string, hide: boolean) =>
    setHidden((current) => {
      const next = new Set(current);
      if (hide) next.add(taskId);
      else next.delete(taskId);
      return next;
    });
  /** The board's first done-kind column, from the boards already cached; none while they load. */
  const doneColumnOf = (boardId: string) => {
    const board = boardsQuery.data?.boards.find((detail) => detail.board.id === boardId);
    return board ? doneTarget(board.columns) : null;
  };
  const markDone = async (review: WaitingReviewCard, targetId: string) => {
    setRowHidden(review.taskId, true);
    const moved = await quickMove({
      taskId: review.taskId,
      title: review.title,
      fromColumnId: review.columnId,
      toColumnId: targetId,
      verb: "Done",
      onUndone: () => setRowHidden(review.taskId, false),
    });
    if (!moved) setRowHidden(review.taskId, false);
  };
  const toggle = () =>
    setOpen((value) => {
      localStorage.setItem(storageKey, value ? "0" : "1");
      return !value;
    });
  const groups = new Map<string, { name: string; cards: WaitingReviewCard[] }>();
  const visible = reviews.filter((review) => !hidden.has(review.taskId));
  if (visible.length === 0) return null;
  for (const review of visible) {
    const group = groups.get(review.boardId) ?? { name: review.boardName, cards: [] };
    group.cards.push(review);
    groups.set(review.boardId, group);
  }
  return (
    <section className="border-t border-border/60 pt-4">
      <button onClick={toggle} aria-expanded={open} className="mb-1.5 flex w-full items-center gap-2 text-left">
        <ClipboardCheck className="size-4 shrink-0" style={{ color: "var(--kind-review)" }} />
        <span className="text-[13px] font-semibold">{title}</span>
        <span
          className="rounded-full px-1.5 text-[11px] font-semibold tabular-nums"
          style={{ color: "var(--kind-review)", backgroundColor: "color-mix(in oklab, var(--kind-review) 14%, transparent)" }}
        >
          {visible.length}
        </span>
        <span className="min-w-0 flex-1 truncate text-[12px] text-muted-foreground">
          {[...groups.values()].map((group) => `${group.name} ${group.cards.length}`).join(" · ")}
        </span>
        <ChevronRight className={cn("size-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-90")} />
      </button>
      {open ? (
        <div className="space-y-3">
          {[...groups.entries()].map(([boardId, group]) => (
            <div key={boardId}>
              <GroupHeading label={`On ${group.name}`} count={group.cards.length} />
              <ul className="divide-y divide-border/50 overflow-hidden rounded-md border border-border/70 bg-card">
                {group.cards.map((review) => (
                  <li key={review.taskId}>
                    <ReviewRow
                      review={review}
                      users={users}
                      now={now}
                      onOpen={() => onOpenTask(review.taskId)}
                      onDone={(() => {
                        const target = canMove ? doneColumnOf(review.boardId) : null;
                        return target ? () => void markDone(review, target.id) : undefined;
                      })()}
                    />
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}

/** One card to review: a slim row, since there can be dozens. */
export function ReviewRow({
  review,
  users,
  now,
  onOpen,
  onDone,
}: {
  review: WaitingReviewCard;
  users: User[];
  now: number;
  onOpen: () => void;
  /** Moves the card to its board's done column without opening it; absent for a viewer. */
  onDone?: () => void;
}) {
  const assignee = review.assigneeId ? users.find((user) => user.id === review.assigneeId) : undefined;
  return (
    <div className="group/review flex items-center transition-colors hover:bg-muted/40">
      <button onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-2.5 py-2 pl-3 pr-2 text-left">
        <span aria-hidden className="h-6 w-[3px] shrink-0 rounded-full" style={{ backgroundColor: priorityColor(review.priority) }} />
        <span className="min-w-0 flex-1 truncate text-[13px]">{review.title}</span>
        {assignee ? <span className="hidden shrink-0 truncate text-[11px] text-muted-foreground win-sm:inline">{assignee.displayName}</span> : null}
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{relativeTime(review.since, now)}</span>
      </button>
      {onDone ? (
        <button
          type="button"
          onClick={onDone}
          aria-label={`Mark done: ${review.title}`}
          title="Mark done"
          className={cn(
            "mr-1.5 inline-flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[12px] font-medium text-muted-foreground transition-[opacity,background-color,color]",
            "hover:bg-primary/12 hover:text-primary focus-visible:opacity-100",
            // Shown on hover or focus with a mouse; always with a finger, which has no hover.
            "[@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover/review:opacity-100 [@media(hover:hover)]:group-focus-within/review:opacity-100",
          )}
        >
          <Check className="size-3.5" />
          Done
        </button>
      ) : null}
    </div>
  );
}

function GroupHeading({ label, count }: { label: string; count: number }) {
  return (
    <h2 className="mb-1.5 text-[12px] font-medium uppercase tracking-wide text-muted-foreground">
      {label} <span className="tabular-nums">({count})</span>
    </h2>
  );
}

const ROW_CLASS = cn(
  "group flex w-full items-start gap-3 rounded-md border border-waiting/35 bg-card px-3 py-2.5 text-left",
  "transition-all hover:-translate-y-px hover:border-waiting/70 hover:shadow-[0_6px_16px_-10px_var(--waiting)]",
);

/** An agent's question asked in its chat: the same row, opening the chat where it is answered. */
function ChatQuestionRow({
  question,
  agents,
  users,
  now,
  onOpen,
  onOpenTask,
  onDismissed,
}: {
  question: WaitingChatQuestion;
  agents: AgentWithContext[];
  users: User[];
  now: number;
  onOpen: () => void;
  onOpenTask: (taskId: string) => void;
  onDismissed?: () => void;
}) {
  const [dismissing, setDismissing] = useState(false);
  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.respondToAgent(question.agentId, question.messageId, { action: "dismiss" });
      toast.success("Marked no longer needed");
      onDismissed?.();
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "Could not close the question");
    } finally {
      setDismissing(false);
    }
  };
  const agent = agents.find((candidate) => candidate.id === question.agentId);
  const agentUser = agent ? undefined : users.find((user) => user.id === question.agentId);
  const firstLine = questionTitle(question.body);
  return (
    <button onClick={onOpen} className={ROW_CLASS}>
      <span className="mt-0.5 shrink-0">
        {agent ? <AgentAvatar agent={agent} size="sm" /> : agentUser ? <Avatar {...avatarOf(agentUser)} size="sm" /> : null}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p className="min-w-0 flex-1 truncate text-[13.5px] font-medium">{question.agentName}</p>
          <QuestionAge since={question.since} now={now} />
        </div>
        <p className="mt-0.5 text-[11px] text-muted-foreground">@{question.agentHandle} · in its chat</p>
        <p className="mt-1 line-clamp-2 text-[12.5px] text-card-foreground/90">{firstLine}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-full bg-waiting/12 px-1.5 py-px text-[10.5px] font-medium text-waiting">
            <HelpCircle className="size-3" />
            {WAITING_REASON_LABELS.question}
          </span>
          {question.options.map((option) => (
            <span key={option} className="rounded-full border border-border/70 px-1.5 py-px text-[10.5px] text-muted-foreground">
              {option}
            </span>
          ))}
          {question.taskId ? <QuestionCardChip title={null} onOpen={() => onOpenTask(question.taskId!)} /> : null}
          <span
            role="button"
            tabIndex={0}
            aria-disabled={dismissing}
            className="ml-auto inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={(event) => {
              event.stopPropagation();
              if (!dismissing) void dismiss();
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" && event.key !== " ") return;
              event.preventDefault();
              event.stopPropagation();
              if (!dismissing) void dismiss();
            }}
          >
            {dismissing ? <Loader2 className="size-3 animate-spin" /> : <X className="size-3" />} No longer needed
          </span>
        </div>
      </div>
    </button>
  );
}

function WaitingRow({
  card,
  agents,
  users,
  now,
  onOpen,
}: {
  card: WaitingCard;
  agents: AgentWithContext[];
  users: User[];
  now: number;
  onOpen: () => void;
}) {
  // One asker per row is enough to say who is waiting; the sheet lists the rest.
  const asker = questionsOf(card)[0];
  const agent = asker ? agents.find((candidate) => candidate.id === asker.agentId) : undefined;
  const agentUser = asker && !agent ? users.find((user) => user.id === asker.agentId) : undefined;
  return (
    <button onClick={onOpen} className={ROW_CLASS}>
      <span aria-hidden className="mt-0.5 h-10 w-[3px] shrink-0 rounded-full" style={{ backgroundColor: priorityColor(card.priority) }} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <p className="min-w-0 flex-1 truncate text-[13.5px] font-medium">{card.title}</p>
          <QuestionAge since={card.since} now={now} />
        </div>
        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
          <span className="truncate">{card.boardName}</span>
          <span className="opacity-40">·</span>
          <span style={{ color: kindColor(card.columnKind) }}>{card.columnName}</span>
        </p>
        {card.summary ? <p className="mt-1 line-clamp-1 text-[12.5px] text-card-foreground/90">{card.summary}</p> : null}
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          {asker ? (
            <span className="mr-0.5 inline-flex min-w-0 items-center gap-1.5 text-[11.5px] font-medium">
              {agent ? (
                <AgentAvatar agent={agent} size="sm" />
              ) : agentUser ? (
                <Avatar {...avatarOf(agentUser)} size="sm" />
              ) : null}
              <span className="truncate">{asker.agentName}</span>
            </span>
          ) : null}
          {reasonKinds(card).map((kind) => {
            const Icon = REASON_ICON[kind];
            return (
              <span
                key={kind}
                className="inline-flex items-center gap-1 rounded-full bg-waiting/12 px-1.5 py-px text-[10.5px] font-medium text-waiting"
              >
                <Icon className="size-3" />
                {WAITING_REASON_LABELS[kind]}
              </span>
            );
          })}
        </div>
      </div>
    </button>
  );
}
